import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import {
  Badge,
  Button,
  Callout,
  Card,
  Explain,
  Loading,
  Modal,
  Mono,
  Screen,
} from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { statusLabel } from '../../lib/status-label.js';
import { useTypingFocus } from '../../lib/typing-focus.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { SearchSelect } from '../../components/search-select.js';
import { CountSheet } from './count-sheet.js';
import './counts.css';

/**
 * Cycle counts (SCOPE §6.4, decision 124).
 *
 * Three states, one screen each, because they are three different jobs:
 *
 *  - **Counting** is done with a scanner in one hand. The scan box holds focus
 *    and every scan is one more; a full box is typed. It is a **blind count**:
 *    what the books expect is not on this screen, because a counter who can see
 *    "12" tends to find twelve. The drawer is counted the same way at end of
 *    day, for the same reason.
 *  - **Reviewing** is where the books appear, and only where they disagree —
 *    a list of forty sizes that matched is forty rows of nothing to decide.
 *  - **Posting** is a manager's act (the same gate as a write-off), asked for
 *    inline rather than by sending somebody to find an admin (§2.2-11).
 *
 * Only what was counted is ever corrected, and it is corrected by the
 * *difference* — see `services/counts.ts` for why that matters in a shop that
 * keeps trading while the count goes on.
 */

type CountView = Awaited<ReturnType<typeof api.counts.get.query>>;

const STATUS_TONE = {
  open: 'info',
  in_review: 'warning',
  posted: 'success',
  cancelled: 'neutral',
} as const;

const STATUS_WORDS: Record<string, string> = {
  open: 'counting',
  in_review: 'in review',
};

function statusWords(status: string): string {
  return STATUS_WORDS[status] ?? statusLabel(status);
}

/**
 * A difference at cost, said with a word beside it — never the sign alone.
 *
 * Reports → Adjustments reads a loss as positive (it is a loss report); a
 * count is the other way round (counted less the books). Two screens a
 * manager moves between must not ask them to remember which sign means what,
 * so neither figure here is bare: "$329.96 over", "$164.98 short"
 * (DESIGN-DNA Part 3 — colour, and sign, are never the only carrier).
 */
function costWords(cents: number): string {
  if (cents === 0) return money(0);
  return cents > 0 ? `${money(cents)} over` : `${money(-cents)} short`;
}

export function CountsScreen({ nav }: { nav?: ReactNode }) {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Coming back to the list always re-asks for it: a count that was just
  // posted or canceled must not be missing, or stale, in the list behind it.
  const backToList = () => {
    void queryClient.invalidateQueries({ queryKey: ['counts', 'list'] });
    setOpenId(null);
  };

  // Pressing the tab you are on goes back to the list (decision 110).
  useNavReset(backToList);

  const list = useQuery({
    queryKey: ['counts', 'list'],
    queryFn: () => api.counts.list.query(),
    enabled: openId === null,
  });

  if (openId !== null) {
    return <CountDetail nav={nav} sessionId={openId} onBack={backToList} />;
  }

  return (
    <Screen
      title="Counts"
      subtitle="Count a shelf or a wall, see where it disagrees with the books, and correct them."
      width="wide"
      primaryAction={
        <Button variant="primary" onClick={() => setStarting(true)}>
          Start a count
        </Button>
      }
    >
      {nav}
      {list.isError ? <Callout tone="danger">{errorMessage(list.error)}</Callout> : null}
      {list.isPending ? <Loading label="Loading the counts" /> : null}

      {list.data !== undefined && list.data.length === 0 ? (
        <Card>
          <p className="text-secondary">
            No counts yet. A count is a shelf, a wall or a department — whatever you can finish in
            one go. Only what you count is corrected; nothing else is touched.
          </p>
        </Card>
      ) : null}

      {list.data !== undefined && list.data.length > 0 ? (
        <Card padded={false}>
          <table className="table">
            <thead>
              <tr>
                <th>Count</th>
                <th>What</th>
                <th>Status</th>
                <th className="num">Sizes counted</th>
                <th className="num">Differing</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((count) => (
                <tr key={count.id}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setOpenId(count.id)}
                    >
                      <Mono>{count.number}</Mono>
                    </button>
                  </td>
                  <td>{count.name ?? <span className="text-secondary">Not named</span>}</td>
                  <td>
                    <Badge
                      tone={STATUS_TONE[count.status as keyof typeof STATUS_TONE] ?? 'neutral'}
                    >
                      {statusWords(count.status)}
                    </Badge>
                  </td>
                  <td className="num">{count.sizes}</td>
                  <td className="num">{count.differing}</td>
                  <td className="text-secondary">
                    {new Date(count.startedAt).toLocaleDateString()}
                    {count.startedBy === null ? '' : ` · ${count.startedBy}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      {starting ? (
        <StartCountDialog
          onClose={() => setStarting(false)}
          onStarted={(id) => {
            setStarting(false);
            setOpenId(id);
          }}
        />
      ) : null}
    </Screen>
  );
}

function StartCountDialog({
  onClose,
  onStarted,
}: {
  onClose: () => void;
  onStarted: (sessionId: string) => void;
}) {
  const [name, setName] = useState('');
  /*
   * A count of whatever is scanned, or a sheet for **a category, then its
   * vendors** — all of them unless some are ticked (client, 2026-10-16: "when
   * you select a category we don't need to select the higher level online
   * webstore categories we just need the sub level … choose a category then
   * select all vendors in that category default, or just select a couple of
   * vendors"; decision 220). Only the categories models are filed in are
   * offered, by their paths.
   */
  const [kind, setKind] = useState<'scan' | 'sheet'>('scan');
  const [categoryId, setCategoryId] = useState('');
  const [allVendors, setAllVendors] = useState(true);
  const [vendorIds, setVendorIds] = useState<readonly string[]>([]);
  const [vendorFind, setVendorFind] = useState('');
  const choices = useQuery({
    queryKey: ['counts', 'sheetChoices'],
    queryFn: () => api.counts.sheetChoices.query(),
    staleTime: 60_000,
    enabled: kind === 'sheet',
  });
  const category = (choices.data ?? []).find((entry) => entry.id === categoryId);
  const shownVendors = (category?.vendors ?? []).filter((vendor) =>
    vendor.name.toLowerCase().includes(vendorFind.trim().toLowerCase()),
  );
  const sheetProblem =
    kind !== 'sheet'
      ? undefined
      : categoryId === ''
        ? 'Choose the category'
        : !allVendors && vendorIds.length === 0
          ? 'Tick at least one vendor'
          : undefined;
  const start = useMutation({
    mutationFn: () =>
      api.counts.start.mutate({
        name: name.trim() === '' ? null : name.trim(),
        ...(kind === 'scan'
          ? {}
          : {
              scope: {
                kind: 'category' as const,
                id: categoryId,
                ...(allVendors ? {} : { vendorIds: [...vendorIds] }),
              },
            }),
      }),
    onSuccess: (created) => {
      recordBreadcrumb('stock', `started count ${created.number}`);
      onStarted(created.id);
    },
  });

  return (
    <Modal
      open
      title="What are you counting?"
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Not now
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={start.isPending}
          disabledReason={sheetProblem}
          onClick={() => start.mutate()}
        >
          {kind === 'scan' ? 'Start counting' : 'Start the sheet'}
        </Button>
      }
    >
      <fieldset className="counts__kind">
        <legend className="field__label">How</legend>
        <label className="toggle">
          <input
            type="radio"
            name="count-kind"
            checked={kind === 'scan'}
            onChange={() => setKind('scan')}
          />
          <span>Scan what I find — one scan per unit</span>
        </label>
        <label className="toggle">
          <input
            type="radio"
            name="count-kind"
            checked={kind === 'sheet'}
            onChange={() => setKind('sheet')}
          />
          <span>A count sheet for a category and its vendors — printed, or typed in on screen</span>
        </label>
      </fieldset>
      {kind === 'sheet' ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="count-scope">
              Which category
            </label>
            <SearchSelect
              id="count-scope"
              options={(choices.data ?? []).map((entry) => ({
                value: entry.id,
                label: `${entry.path} (${entry.models.toLocaleString()} ${entry.models === 1 ? 'model' : 'models'})`,
              }))}
              value={categoryId}
              onChange={(value) => {
                setCategoryId(value);
                setAllVendors(true);
                setVendorIds([]);
                setVendorFind('');
              }}
              placeholder="Pick a category — type to search"
            />
          </div>
          {category !== undefined ? (
            <fieldset className="counts__vendors">
              <legend className="field__label">Which vendors</legend>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={allVendors}
                  onChange={(event) => setAllVendors(event.target.checked)}
                />
                <span>
                  {category.vendors.length === 1
                    ? `${category.vendors[0]!.name}, the only vendor in ${category.path}`
                    : `All ${category.vendors.length} vendors in ${category.path}`}
                </span>
              </label>
              {allVendors ? null : (
                <>
                  {category.vendors.length > 8 ? (
                    <input
                      className="input"
                      aria-label="Find a vendor"
                      placeholder="Find a vendor"
                      value={vendorFind}
                      onChange={(event) => setVendorFind(event.target.value)}
                    />
                  ) : null}
                  <ul className="counts__vendor-list">
                    {shownVendors.map((vendor) => (
                      <li key={vendor.id}>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={vendorIds.includes(vendor.id)}
                            onChange={(event) =>
                              setVendorIds(
                                event.target.checked
                                  ? [...vendorIds, vendor.id]
                                  : vendorIds.filter((id) => id !== vendor.id),
                              )
                            }
                          />
                          <span>
                            {vendor.name}{' '}
                            <span className="text-secondary">
                              {vendor.models.toLocaleString()}{' '}
                              {vendor.models === 1 ? 'model' : 'models'}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </fieldset>
          ) : null}
        </>
      ) : null}
      <div className="field">
        <label className="field__label" htmlFor="count-name">
          A name you will recognize later
        </label>
        <input
          id="count-name"
          className="input"
          autoFocus
          maxLength={60}
          placeholder="Goggle wall"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              start.mutate();
            }
          }}
        />
        <p className="field__hint">
          Optional. The count gets a number either way
          {kind === 'scan' ? '' : ', and a sheet is called what it covers'}.
        </p>
      </div>
      {start.isError ? <Callout tone="danger">{errorMessage(start.error)}</Callout> : null}
    </Modal>
  );
}

function CountDetail({
  nav,
  sessionId,
  onBack,
}: {
  nav?: ReactNode;
  sessionId: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const key = ['counts', 'get', sessionId];
  const count = useQuery({ queryKey: key, queryFn: () => api.counts.get.query({ sessionId }) });
  const accept = (view: CountView) => queryClient.setQueryData(key, view);

  const setStatus = useMutation({
    mutationFn: (to: 'open' | 'in_review' | 'cancelled') =>
      api.counts.setStatus.mutate({ sessionId, to }),
    onSuccess: (view, to) => {
      accept(view);
      if (to === 'cancelled') {
        notify({
          title: `Count ${view.session.number} canceled`,
          detail: 'Nothing on the shelf or in the books changed.',
        });
        onBack();
      }
    },
  });

  if (count.isPending) {
    return (
      <Screen title="Count" width="wide">
        {nav}
        <Loading label="Loading the count" />
      </Screen>
    );
  }
  if (count.isError) {
    return (
      <Screen
        title="Count"
        width="wide"
        secondaryActions={
          <Button variant="ghost" onClick={onBack}>
            Back to counts
          </Button>
        }
      >
        {nav}
        <Callout tone="danger">{errorMessage(count.error)}</Callout>
      </Screen>
    );
  }

  const view = count.data;
  const title = (
    <>
      <Mono>{view.session.number}</Mono>
      {view.session.name === null ? '' : ` · ${view.session.name}`}
    </>
  );
  const back = (
    <Button variant="ghost" onClick={onBack}>
      Back to counts
    </Button>
  );

  if (view.session.status === 'open') {
    return (
      <Screen
        title={title}
        subtitle={
          view.session.scope !== null
            ? 'Print the sheet and write in only what is off. Back here, scan the item’s label or a printed row’s barcode, type the count, press Enter — then Mark the rest as matching.'
            : 'Scan each item, one scan per unit. Type the count for a full box.'
        }
        width="wide"
        primaryAction={
          <Button
            variant="primary"
            busy={setStatus.isPending}
            disabledReason={view.lines.length === 0 ? 'Count something first' : undefined}
            onClick={() => setStatus.mutate('in_review')}
          >
            Review the differences
          </Button>
        }
        secondaryActions={back}
        destructiveAction={
          <Button variant="danger" onClick={() => setStatus.mutate('cancelled')}>
            Cancel count
          </Button>
        }
      >
        {nav}
        {setStatus.isError ? (
          <Callout tone="danger">{errorMessage(setStatus.error)}</Callout>
        ) : null}
        {/* A sheet count is typed into its sheet; a scan count counts one per beep. */}
        {view.session.scope !== null ? (
          <CountSheet sessionId={sessionId} number={view.session.number} open onChanged={accept} />
        ) : (
          <Counting view={view} sessionId={sessionId} onChanged={accept} />
        )}
      </Screen>
    );
  }

  if (view.session.status === 'in_review') {
    return (
      <Reviewing
        nav={nav}
        title={title}
        view={view}
        sessionId={sessionId}
        onChanged={accept}
        onBackToCounting={() => setStatus.mutate('open')}
        onBack={onBack}
      />
    );
  }

  return (
    <Screen title={title} width="wide" secondaryActions={back}>
      {nav}
      <Callout tone={view.session.status === 'posted' ? 'success' : 'neutral'}>
        {view.session.status === 'posted'
          ? `Posted${view.session.postedAt === null ? '' : ` on ${new Date(view.session.postedAt).toLocaleDateString()}`}. The corrections are in Reports → Adjustments under Count correction.`
          : 'This count was canceled. Nothing was changed.'}
      </Callout>
      <Differences view={view} />
    </Screen>
  );
}

function Counting({
  view,
  sessionId,
  onChanged,
}: {
  view: CountView;
  sessionId: string;
  onChanged: (view: CountView) => void;
}) {
  const [code, setCode] = useState('');
  const [last, setLast] = useState<string | null>(null);
  const box = useRef<HTMLInputElement>(null);
  /** A scan with the caret anywhere on the count lands in its box (client, 2026-10-15). */
  const appendScanned = useCallback(
    (character: string) => setCode((current) => current + character),
    [],
  );
  useTypingFocus(box, appendScanned);

  const scan = useMutation({
    mutationFn: (scanned: string) => api.counts.scan.mutate({ sessionId, code: scanned }),
    onSuccess: (next) => {
      onChanged(next);
      setLast(next.lines[0]?.variantId ?? null);
      setCode('');
      box.current?.focus();
    },
    /*
     * A refused code stays in the box so it can be read against the refusal —
     * and is selected, so the next beep replaces it. A USB scanner is a
     * keyboard: left unselected, the next scan would be typed on the end of
     * the failed one and refused too, and so on down the shelf.
     */
    onError: () => {
      box.current?.focus();
      box.current?.select();
    },
  });

  const setCount = useMutation({
    mutationFn: (input: { variantId: string; countedQty: number }) =>
      api.counts.setCount.mutate({ sessionId, ...input }),
    onSuccess: onChanged,
  });

  const remove = useMutation({
    mutationFn: (variantId: string) => api.counts.removeLine.mutate({ sessionId, variantId }),
    onSuccess: onChanged,
  });

  // A scanner is a keyboard: keep the box ready for the next beep (§2-1).
  useEffect(() => {
    box.current?.focus();
  }, []);

  return (
    <>
      <form
        className="counts__scan"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim() !== '') scan.mutate(code.trim());
        }}
      >
        <label className="visually-hidden" htmlFor="count-scan">
          Scan or type a barcode or SKU
        </label>
        <input
          id="count-scan"
          ref={box}
          className="search__input"
          placeholder="Scan a barcode, or type a SKU and press Enter"
          autoComplete="off"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
      </form>

      {scan.isError ? <Callout tone="danger">{errorMessage(scan.error)}</Callout> : null}
      {setCount.isError ? <Callout tone="danger">{errorMessage(setCount.error)}</Callout> : null}

      <p className="counts__tally" role="status">
        {view.summary.unitsCounted} unit{view.summary.unitsCounted === 1 ? '' : 's'} across{' '}
        {view.summary.sizesCounted} size{view.summary.sizesCounted === 1 ? '' : 's'}
      </p>

      {view.lines.length === 0 ? (
        <Card>
          <p className="text-secondary">
            Nothing counted yet. What the books expect is not shown while you count — you will see
            it, and only where it differs, when you review.
          </p>
        </Card>
      ) : (
        <Card padded={false}>
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th>SKU</th>
                <th className="num">Counted</th>
                <th className="table__actions" />
              </tr>
            </thead>
            <tbody>
              {view.lines.map((line) => (
                <tr key={line.variantId} className={line.variantId === last ? 'counts__last' : ''}>
                  <td>{line.description}</td>
                  <td>
                    <Mono>{line.sku}</Mono>
                  </td>
                  <td className="num">
                    <CountBox
                      label={`Counted ${line.description}`}
                      value={line.countedQty}
                      onCommit={(countedQty) =>
                        setCount.mutate({ variantId: line.variantId, countedQty })
                      }
                    />
                  </td>
                  <td className="table__actions">
                    <Button variant="ghost" onClick={() => remove.mutate(line.variantId)}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

/** A count you can type over. Committed on blur or Enter, never per keystroke (§6). */
function CountBox({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = () => {
    const parsed = /^\d+$/.test(draft.trim()) ? Number(draft.trim()) : null;
    if (parsed === null) setDraft(String(value));
    else if (parsed !== value) onCommit(parsed);
  };

  return (
    <input
      className="input counts__box"
      inputMode="numeric"
      aria-label={label}
      value={draft}
      onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ''))}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}

function Reviewing({
  nav,
  title,
  view,
  sessionId,
  onChanged,
  onBackToCounting,
  onBack,
}: {
  nav?: ReactNode;
  title: ReactNode;
  view: CountView;
  sessionId: string;
  onChanged: (view: CountView) => void;
  onBackToCounting: () => void;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [needsApproval, setNeedsApproval] = useState(false);

  const post = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.counts.post.mutate({ sessionId, ...(override === undefined ? {} : { override }) }),
    onSuccess: (result) => {
      recordBreadcrumb('stock', `posted count ${view.session.number}: ${result.corrected} sizes`);
      notify({
        title:
          result.corrected === 0
            ? `Count ${view.session.number} closed — everything agreed`
            : `Count ${view.session.number} posted — ${result.corrected} ${
                result.corrected === 1 ? 'size' : 'sizes'
              } corrected`,
      });
      setNeedsApproval(false);
      onChanged(result.count);
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['counts', 'list'] });
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  const differing = view.summary.sizesDiffering;

  return (
    <Screen
      title={title}
      subtitle="Where the count and the books disagree. Posting corrects the books by the difference."
      width="wide"
      primaryAction={
        <Button variant="primary" busy={post.isPending} onClick={() => post.mutate(undefined)}>
          {differing === 0
            ? 'Close the count — everything agreed'
            : `Post ${differing} correction${differing === 1 ? '' : 's'}`}
        </Button>
      }
      secondaryActions={
        <>
          <Button variant="ghost" onClick={onBack}>
            Back to counts
          </Button>
          <Button variant="secondary" onClick={onBackToCounting}>
            Back to counting
          </Button>
        </>
      }
    >
      {nav}
      {post.isError && !isOverridable(post.error) ? (
        <Callout tone="danger">{errorMessage(post.error)}</Callout>
      ) : null}

      <div className="counts__summary">
        <Figure label="Sizes counted" value={String(view.summary.sizesCounted)} />
        <Figure label="Agreed" value={String(view.summary.sizesCounted - differing)} />
        <Figure label="Units short" value={String(view.summary.unitsShort)} />
        <Figure label="Units over" value={String(view.summary.unitsOver)} />
        <Figure
          label="Net at cost"
          value={costWords(view.summary.netCostCents)}
          explain="Each size's difference — counted less what the books said — at its current average cost, added up. Over means the shelf holds more than the books; short means less. Once posted it appears in Reports → Adjustments under Count correction, where a loss reads as a positive cost."
        />
      </div>

      <Differences view={view} />

      {needsApproval ? (
        <ManagerApproval
          title="Manager approval needed"
          because="Posting a count changes what the books say the shop owns."
          busy={post.isPending}
          error={post.isError ? approvalRefusal(post.error) : null}
          onCancel={() => setNeedsApproval(false)}
          onApprove={(override) => post.mutate(override)}
        />
      ) : null}
    </Screen>
  );
}

function Figure({ label, value, explain }: { label: string; value: string; explain?: string }) {
  return (
    <Card>
      <div className="label-micro">
        {label}
        {explain === undefined ? null : <Explain label={label.toLowerCase()}>{explain}</Explain>}
      </div>
      <div className="counts__figure money">{value}</div>
    </Card>
  );
}

function Differences({ view }: { view: CountView }) {
  const differing = view.lines.filter((line) => line.variance !== 0);
  const agreed = view.lines.length - differing.length;

  if (differing.length === 0) {
    return (
      <Card>
        <p className="text-secondary">
          Every one of the {view.lines.length} size{view.lines.length === 1 ? '' : 's'} counted
          matched the books.
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card padded={false}>
        <table className="table">
          <thead>
            <tr>
              <th>Item</th>
              <th>SKU</th>
              <th className="num">Books said</th>
              <th className="num">Counted</th>
              <th className="num">Difference</th>
              <th className="num">Difference at cost</th>
            </tr>
          </thead>
          <tbody>
            {differing.map((line) => (
              <tr key={line.variantId}>
                <td>{line.description}</td>
                <td>
                  <Mono>{line.sku}</Mono>
                </td>
                <td className="num">{line.expectedQty}</td>
                <td className="num">{line.countedQty}</td>
                <td className="num">
                  {/* A sign and a word, never colour alone (DESIGN-DNA Part 3). */}
                  {line.variance > 0 ? `+${line.variance} over` : `${line.variance} short`}
                </td>
                <td className="num money">{costWords(line.varianceCostCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {agreed > 0 ? (
        <p className="text-secondary">
          {agreed} other size{agreed === 1 ? '' : 's'} matched the books and{' '}
          {agreed === 1 ? 'is' : 'are'} not listed.
        </p>
      ) : null}
    </>
  );
}
